<template>
  <v-form v-if="data" :readonly="readonly" ref="form">
    <v-card elevation="1" class="mb-2">
      <v-card-title> Информация по препарату </v-card-title>

      <v-card-subtitle v-if="editMode && needRefresh">
        <ReferenceTip>
          Обновить данные из экспертизы до актуальной версии можно кнопкой
          "обновить" ( <v-icon color="green" small>mdi-sync</v-icon> )
        </ReferenceTip>
      </v-card-subtitle>

      <v-card-text>
        <v-alert v-if="needRefresh" type="warning" dense text>
          Внимание! Информация по экспертизе изменилась, рекомендуется обновить
          данные
          <template #append>
            <v-btn
              v-if="editMode"
              text
              color="green"
              small
              title="Обновить все данные по экспертизе"
              @click="refreshData"
              :disabled="!needRefresh"
              ><v-icon>mdi-sync</v-icon></v-btn
            >
          </template>
        </v-alert>
        <!-- Информация по препарату -->
        <v-timeline dense align-top>
          <!-- МНН -->
          <v-timeline-item small>
            <check-alert
              label="МНН"
              v-model="data.mnn"
              :check-value="initMnn"
              :hide-check="logView"
              @change="doChange(true)"
            />
          </v-timeline-item>
          <!-- Перечень -->
          <v-timeline-item small>
            <check-alert
              label="Перечень"
              :value="listOfList"
              :check-value="initListOfList"
              :custom-check="checkList"
              :hide-check="logView"
              @change="listOfListChange"
              empty-value="-- не определён --"
            />
          </v-timeline-item>
          <!-- Лекарственная форма -->
          <v-timeline-item small>
            <check-alert
              label="Лекарственная форма"
              v-model="data.lForma"
              :check-value="initLf"
              :hide-check="logView"
              @change="doChange(true)"
            />
          </v-timeline-item>
          <!-- Дата регистрации ЛП -->
          <v-timeline-item small>
            <check-alert
              label="Дата регистрации ЛП"
              v-model="data.ruDate"
              :check-value="initRuDate"
              :hide-check="logView"
              @change="doChange(true)"
            />
          </v-timeline-item>
          <!-- Предыдущее предложение -->
          <v-timeline-item small>
            <div class="summary-info-field">
              <div class="text-subtitle-2 font-weight-medium mb-2">
                Предыдущее предложение
              </div>
              <v-switch
                v-if="editMode"
                v-model="data.previousProposalSubmitted"
                label="Подавался ли ЛП ранее?"
                color="primary"
                hide-details
                class="mt-0"
                @change="onPreviousProposalToggle"
              />
              <div v-else class="mb-2">
                Подавался ли ЛП ранее:
                <span class="font-weight-medium">{{
                  data.previousProposalSubmitted ? 'Да' : 'Нет'
                }}</span>
              </div>
              <v-textarea
                v-if="showPreviousProposalText"
                v-model="data.previousProposalText"
                :readonly="readonly"
                :outlined="editMode"
                :dense="editMode"
                rows="3"
                auto-grow
                class="mt-2"
                hint="Например, 2 кв. 2024 г. — предложение отклонено"
                persistent-hint
                @input="doChange()"
                @blur="doBlur()"
              />
              <div
                v-else-if="!editMode && !data.previousProposalSubmitted"
                class="text-pre-wrap text--secondary"
              >
                Нет
              </div>
            </div>
          </v-timeline-item>
          <!-- Держатель РУ -->
          <v-timeline-item small>
            <div class="summary-info-field">
              <div class="text-subtitle-2 font-weight-medium mb-2">
                Держатель РУ
              </div>
              <v-textarea
                v-if="editMode"
                v-model="data.ruHolder"
                :readonly="readonly"
                outlined
                dense
                rows="2"
                auto-grow
                hint="Например, АО «Санофи Россия»"
                persistent-hint
                @input="doChange()"
                @blur="doBlur()"
              />
              <div v-else class="text-pre-wrap">
                {{ data.ruHolder || '—' }}
              </div>
            </div>
          </v-timeline-item>
          <!-- Производитель -->
          <v-timeline-item small>
            <div class="summary-info-field">
              <div class="text-subtitle-2 font-weight-medium mb-2">
                Производитель
              </div>
              <v-textarea
                v-if="editMode"
                v-model="data.manufacturer"
                :readonly="readonly"
                outlined
                dense
                rows="7"
                auto-grow
                hint="Например, Производство готовой лекарственной формы — ЮПМ Фармацевтикалс Инк (США), Каталент Микрон Технолоджис Инк. (США)"
                persistent-hint
                @input="doChange()"
                @blur="doBlur()"
              />
              <div v-else class="text-pre-wrap">
                {{ data.manufacturer || '—' }}
              </div>
            </div>
          </v-timeline-item>
          <!-- Критическая промышленная продукция -->
          <v-timeline-item small>
            <div class="summary-info-field">
              <v-checkbox
                v-if="editMode"
                v-model="data.inCriticalIndustrialProductList"
                hide-details
                class="mt-0"
                color="primary"
                @change="doChange(true)"
              >
                <template #label>
                  <span class="text-body-2">
                    Находится ли МНН в перечне критической промышленной
                    продукции в отрасли фарм. промышленности?
                  </span>
                </template>
              </v-checkbox>
              <div v-else>
                Наличие в перечне критической промышленной продукции:
                <span class="font-weight-medium">{{
                  data.inCriticalIndustrialProductList ? 'Да' : 'Нет'
                }}</span>
              </div>
            </div>
          </v-timeline-item>
          <!-- Орфанный статус -->
          <v-timeline-item small>
            <check-alert
              label="Орфанный статус"
              v-model="data.isOrphan"
              :check-value="initOrphan"
              :hide-check="logView"
              @change="doChange(true)"
            >
              <template #default="{ value }">
                {{ value ? 'Да' : 'Нет' }}
              </template>
            </check-alert>
          </v-timeline-item>
          <!-- Наличие в КР -->
          <v-timeline-item small>
            <div class="summary-info-field">
              <summary-cr-presence
                :value="data.crPresence"
                :legacy-cr-names="data.crNames"
                :readonly="readonly"
                :edit-mode="editMode"
                @change="onCrPresenceChange"
                @blur="doBlur"
              />
            </div>
          </v-timeline-item>
          <!-- Показание ЛП (ИМП) -->
          <v-timeline-item small>
            <check-alert
              label="Показание ЛП (ИМП)"
              v-model="data.condition"
              :check-value="initCondition"
              :hide-check="logView"
              pre
              @change="doChange(true)"
            />
          </v-timeline-item>
        </v-timeline>
      </v-card-text>
    </v-card>
  </v-form>
</template>

<script>
import ReferenceTip from '@/components/ReferenceTip'
import CheckAlert from '@/components/CheckAlert'
import ExpSummaryMixin from '../ExpSummaryMixin'
import SummaryCrPresence from '@/components/expSummary/summaryCrPresence/SummaryCrPresence.vue'
import {
  formatCrPresencePresentation,
  normalizeCrPresence,
} from '@/components/expSummary/summaryCrPresence/crPresenceConst'

const textAlert = {
  plp: 'Информация о препарате изменилась',
}

export default {
  name: 'SummaryInfo',
  components: {
    CheckAlert,
    ReferenceTip,
    SummaryCrPresence,
  },
  mixins: [ExpSummaryMixin],
  data: () => ({
    textAlert,
  }),
  computed: {
    skeletonType: () => Array(11).fill('list-item-two-line').join(','),
    showPreviousProposalText() {
      if (!this.data) return false
      if (this.editMode) return this.data.previousProposalSubmitted
      return (
        this.data.previousProposalSubmitted &&
        !!String(this.data.previousProposalText || '').trim()
      )
    },
    // Проверка на актуальность всех данных
    checkData() {
      return (
        this.checkMnn &&
        this.checkList &&
        this.checkCondition &&
        this.checkLf &&
        this.checkRuDate &&
        this.checkOrphan
      )
    },
    initMnn() {
      return this.initData?.about?.mnn ?? ''
    },
    checkMnn() {
      return !this.initData || this.initMnn === this.data?.mnn
    },
    initLf() {
      return this.initData?.about?.lForma ?? ''
    },
    checkLf() {
      return !this.initData || this.initLf === this.data?.lForma
    },
    initRuDate() {
      return this.initData?.about?.ruDate ?? ''
    },
    checkRuDate() {
      return !this.initData || this.initRuDate === this.data?.ruDate
    },
    initOrphan() {
      return this.initData?.about?.isOrphan ?? false
    },
    checkOrphan() {
      return !this.initData || this.initOrphan === this.data?.isOrphan
    },
    listOfList() {
      return this.makeListOfList(this.data)
    },
    initListOfList() {
      return this.makeListOfList(this.initData?.about)
    },
    checkList() {
      return !this.initData || this.listOfList === this.initListOfList
    },
    initCondition() {
      return this.initData?.about?.condition ?? ''
    },
    checkCondition() {
      return !this.initData || this.initCondition === this.data?.condition
    },
    showAlert() {
      return !this.checkData
    },
  },
  mounted() {
    if (this.data && !this.data.crPresence) {
      this.onCrPresenceChange(
        normalizeCrPresence(null, this.data.crNames),
        true
      )
    }
  },
  methods: {
    makeListOfList({ gnvlp, vzn14 } = {}) {
      const resString = []
      if (gnvlp) resString.push('ЖНВЛП')
      if (vzn14) resString.push('14 ВЗН')
      return resString.join(', ')
    },
    listOfListChange() {
      this.data.gnvlp = this.initData?.about?.gnvlp || false
      this.data.vzn14 = this.initData?.about?.vzn14 || false

      this.doChange(true)
    },
    onCrPresenceChange(crPresence, force = false) {
      this.data.crPresence = normalizeCrPresence(crPresence)
      this.data.crNames = formatCrPresencePresentation(this.data.crPresence)
      this.doChange(force)
    },
    onPreviousProposalToggle() {
      if (!this.data.previousProposalSubmitted) {
        this.data.previousProposalText = 'Нет'
      } else if (this.data.previousProposalText === 'Нет') {
        this.data.previousProposalText = ''
      }
      this.doChange(true)
    },
  },
}
</script>

<style scoped>
.summary-info-field {
  max-width: 720px;
}
</style>
